import React, { createContext, useContext } from 'react'
import PropTypes from 'prop-types'
import {
    SmsNumberCampaignContext,
    SmsNumberCampaignContextProvider
} from '../../../sms-enrollment/src/contexts/SmsNumberCampaignContext'

/**
 *
 */
export const SmsRegistrationContext = createContext({
    campaignsWithNumber: [],
    extensionNumbers: [],
    attachedNumber: false,
    isSMSRegistrationCompleted: false,
    loadingSmsNumberCampaignContext: false
})

/**
 * Returns the shared SMS registration details for the current extension.
 * @returns {object} SMS registration context value.
 */
export const useSmsRegistrationContext = () => useContext(SmsRegistrationContext)

const normalizeNumber = number => String(number || '').replace(/\D/g, '')

const getCampaignNumber = campaign => {
    if (!campaign || typeof campaign !== 'object') return campaign
    return campaign.did || campaign.sms_number || campaign.number
}

/**
 * Derives SMS registration details for an extension.
 * @param {object} extension Extension containing its assigned phone numbers.
 * @param {Array} campaignsWithNumber SMS campaigns containing registered numbers.
 * @returns {object} Normalized registration details.
 */
export const getSmsRegistrationDetails = (extension, campaignsWithNumber = []) => {
    const campaigns = campaignsWithNumber || []
    const extensionNumbers = Object.keys(extension?.phone_number || {})
    const normalizedExtensionNumbers = new Set(
        extensionNumbers.map(normalizeNumber).filter(Boolean)
    )
    const attachedNumber = campaigns.some(campaign => {
        const campaignNumber = normalizeNumber(getCampaignNumber(campaign))
        return Boolean(campaignNumber) && normalizedExtensionNumbers.has(campaignNumber)
    })

    return {
        extensionNumbers,
        campaignsWithNumber: campaigns,
        attachedNumber,
        isSMSRegistrationCompleted: campaigns.length > 0
    }
}

const SmsRegistrationDetailsProvider = ({ children, extension }) => {
    const smsNumberCampaignContext = useContext(SmsNumberCampaignContext)
    const {
        campaignsWithNumber,
        loadingSmsNumberCampaignContext = false
    } = smsNumberCampaignContext || {}
    const contextValue = {
        ...getSmsRegistrationDetails(extension, campaignsWithNumber),
        loadingSmsNumberCampaignContext
    }

    return (
        <SmsRegistrationContext.Provider value={contextValue}>
            {children}
        </SmsRegistrationContext.Provider>
    )
}

SmsRegistrationDetailsProvider.propTypes = {
    children: PropTypes.node,
    extension: PropTypes.object.isRequired
}

/**
 * Provides SMS registration details derived from campaign data.
 * @param {object} props Provider properties.
 * @param {React.ReactNode} props.children Descendant components.
 * @param {object} props.extension Current extension.
 * @returns {React.ReactElement} SMS registration provider.
 */
export const SmsRegistrationContextProvider = ({ children, extension }) => (
    <SmsNumberCampaignContextProvider>
        <SmsRegistrationDetailsProvider extension={extension}>
            {children}
        </SmsRegistrationDetailsProvider>
    </SmsNumberCampaignContextProvider>
)

SmsRegistrationContextProvider.propTypes = {
    children: PropTypes.node,
    extension: PropTypes.object.isRequired
}
